import { useCallback, useEffect, useState } from "react";
import { Check, Download, Loader2, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import DayCapacityBars from "@/components/admin/DayCapacityBars";

// Multi-day registration ("Lichtjestour"-style events) presence grid — all
// participants x all event days at once, each cell an inline-editable toggle
// (RouteGroupRow's inline-edit idiom, applied per cell instead of per row), backed
// by GET /admin/events/{event_id}/registrations and
// PATCH /admin/tickets/{ticket_id}/registration-days. A sibling to
// CompanionPresence.jsx, never a modification of it — that component's single-day
// contract stays untouched.
//
// Its per-day capacity bar (registered_count/capacity straight from this same
// endpoint's days[]) now lives in the shared DayCapacityBars component, since
// AdminDashboard.jsx shows the identical meters for a multi-day registration
// event's upcoming days.

export default function MultiDayPresence({ eventId, authAxios, startNumbersEnabled = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCell, setSavingCell] = useState(null); // `${ticketId}-${dayId}` while a toggle is in flight
  const [startNumberDraft, setStartNumberDraft] = useState({}); // ticketId -> in-progress input string
  const [savingStartNumber, setSavingStartNumber] = useState(null); // ticketId while a start-number save is in flight
  const [exporting, setExporting] = useState(false);
  const [resendingOrder, setResendingOrder] = useState(null); // order_id while a resend is in flight

  const downloadExport = async () => {
    setExporting(true);
    try {
      const resp = await authAxios.get(`/admin/reports/${eventId}/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `presentie-${eventId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Export mislukt");
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/registrations`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId, authAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleDay = async (ticket, dayId) => {
    const cellKey = `${ticket.ticket_id}-${dayId}`;
    const currentlyRegistered = ticket.registrations[dayId]?.registered;
    const nextDayIds = currentlyRegistered
      ? Object.keys(ticket.registrations).filter((d) => d !== dayId && ticket.registrations[d].registered)
      : [...Object.keys(ticket.registrations).filter((d) => ticket.registrations[d].registered), dayId];

    // Optimistic update.
    setData((prev) => ({
      ...prev,
      items: prev.items.map((it) =>
        it.ticket_id === ticket.ticket_id
          ? { ...it, registrations: { ...it.registrations, [dayId]: { ...it.registrations[dayId], registered: !currentlyRegistered } } }
          : it,
      ),
    }));
    setSavingCell(cellKey);
    try {
      await authAxios.patch(`/admin/tickets/${ticket.ticket_id}/registration-days`, { event_day_ids: nextDayIds });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      // Revert on failure.
      setData((prev) => ({
        ...prev,
        items: prev.items.map((it) =>
          it.ticket_id === ticket.ticket_id
            ? { ...it, registrations: { ...it.registrations, [dayId]: { ...it.registrations[dayId], registered: currentlyRegistered } } }
            : it,
        ),
      }));
    } finally {
      setSavingCell(null);
    }
  };

  const startNumberValue = (ticket) =>
    startNumberDraft[ticket.ticket_id] !== undefined
      ? startNumberDraft[ticket.ticket_id]
      : ticket.start_number != null
        ? String(ticket.start_number)
        : "";

  const clearStartNumberDraft = (ticketId) => {
    setStartNumberDraft((prev) => {
      const next = { ...prev };
      delete next[ticketId];
      return next;
    });
  };

  const saveStartNumber = async (ticket) => {
    const raw = startNumberValue(ticket).trim();
    const nextValue = raw === "" ? null : Number(raw);
    if (raw !== "" && (!Number.isInteger(nextValue) || nextValue < 1)) {
      toast.error("Startnummer moet een geheel getal vanaf 1 zijn");
      clearStartNumberDraft(ticket.ticket_id);
      return;
    }
    if (nextValue === (ticket.start_number ?? null)) {
      clearStartNumberDraft(ticket.ticket_id);
      return;
    }
    setSavingStartNumber(ticket.ticket_id);
    try {
      await authAxios.patch(`/admin/tickets/${ticket.ticket_id}/participant`, { start_number: nextValue });
      setData((prev) => ({
        ...prev,
        items: prev.items.map((it) => (it.ticket_id === ticket.ticket_id ? { ...it, start_number: nextValue } : it)),
      }));
      clearStartNumberDraft(ticket.ticket_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      clearStartNumberDraft(ticket.ticket_id);
    } finally {
      setSavingStartNumber(null);
    }
  };

  const resendConfirmation = async (ticket) => {
    if (!ticket.order_id) return;
    setResendingOrder(ticket.order_id);
    try {
      await authAxios.post(`/admin/orders/${ticket.order_id}/resend-email`);
      toast.success("Bevestigingsmail opnieuw verstuurd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setResendingOrder(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="text-sm text-slate-400 inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Presentie laden…
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return <div className="text-sm text-slate-400">Nog geen aanmeldingen voor dit event.</div>;
  }

  return (
    <div>
      <DayCapacityBars days={data.days} />
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-400">
          {data.items.length} deelnemer{data.items.length === 1 ? "" : "s"} · {data.days.length} dag{data.days.length === 1 ? "" : "en"}
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={downloadExport}
            disabled={exporting}
            data-testid="mdr-presence-export"
            className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 disabled:opacity-60"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Exporteren
          </button>
          <button type="button" onClick={load} data-testid="mdr-presence-refresh" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">
            <RefreshCw className="h-3.5 w-3.5" /> Vernieuwen
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mb-3 text-[11px] text-slate-400" data-testid="mdr-presence-legend">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-4 w-4 rounded-md bg-emerald-500 border border-emerald-500 inline-flex items-center justify-center">
            <Check className="h-3 w-3 text-white" strokeWidth={3} />
          </span>
          Ingecheckt (gescand)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-4 w-4 rounded-md bg-svj-cyan/20 border border-svj-cyan/60 inline-flex items-center justify-center">
            <Check className="h-3 w-3 text-svj-cyan" strokeWidth={3} />
          </span>
          Aangemeld, nog niet gescand
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-4 w-4 rounded-md bg-svj-bg border border-svj-border inline-block" />
          Niet aangemeld voor deze dag
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="mdr-presence-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-svj-border">
              {startNumbersEnabled && <th className="py-2 pr-3 font-semibold whitespace-nowrap">Startnr.</th>}
              <th className="py-2 pr-3 font-semibold">Deelnemer</th>
              {data.days.map((d) => (
                <th key={d.id} className="py-2 px-2 font-semibold text-center whitespace-nowrap">
                  Dag {d.day_number}{d.label ? <span className="block normal-case text-[10px] text-slate-500">{d.label}</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-svj-border">
            {data.items.map((ticket) => (
              <tr key={ticket.ticket_id} data-testid={`mdr-presence-row-${ticket.ticket_id}`}>
                {startNumbersEnabled && (
                  <td className="py-2 pr-3">
                    <input
                      type="number"
                      min={1}
                      value={startNumberValue(ticket)}
                      onChange={(e) => setStartNumberDraft((prev) => ({ ...prev, [ticket.ticket_id]: e.target.value }))}
                      onBlur={() => saveStartNumber(ticket)}
                      disabled={savingStartNumber === ticket.ticket_id}
                      data-testid={`mdr-presence-start-number-${ticket.ticket_id}`}
                      className="w-16 h-8 px-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm text-center focus:outline-none focus:ring-2 focus:ring-svj-cyan disabled:opacity-60"
                    />
                  </td>
                )}
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-1.5">
                    <div>
                      <div className="text-white font-medium">{ticket.holder_name || ticket.ticket_name}</div>
                      <div className="text-[11px] text-slate-500">{ticket.code}</div>
                    </div>
                    {ticket.order_id && (
                      <button
                        type="button"
                        onClick={() => resendConfirmation(ticket)}
                        disabled={resendingOrder === ticket.order_id}
                        data-testid={`mdr-presence-resend-${ticket.ticket_id}`}
                        title="Verstuur de bevestigingsmail (met eventuele correcties, zoals een gewijzigd startnummer) opnieuw naar deze bestelling"
                        className="text-slate-400 hover:text-svj-cyan disabled:opacity-60 shrink-0"
                      >
                        {resendingOrder === ticket.order_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                </td>
                {data.days.map((d) => {
                  const reg = ticket.registrations[d.id];
                  const cellKey = `${ticket.ticket_id}-${d.id}`;
                  const isSaving = savingCell === cellKey;
                  const isScanned = !!reg?.checked_in_at;
                  return (
                    <td key={d.id} className="py-2 px-2 text-center">
                      <button
                        type="button"
                        onClick={() => toggleDay(ticket, d.id)}
                        disabled={isSaving}
                        data-testid={`mdr-presence-toggle-${ticket.ticket_id}-${d.id}`}
                        title={isScanned ? `Gescand om ${new Date(reg.checked_in_at).toLocaleString("nl-NL")}` : undefined}
                        className={`h-9 w-9 rounded-lg border-2 flex items-center justify-center mx-auto transition-colors shadow-sm ${
                          isScanned
                            ? "bg-emerald-500 border-emerald-500 text-white"
                            : reg?.registered
                              ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                              : "bg-svj-bg border-svj-border text-transparent hover:text-slate-500"
                        }`}
                      >
                        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-5 w-5" strokeWidth={isScanned ? 3 : 2} />}
                      </button>
                      {isScanned && (
                        <div className="mt-1 text-[10px] text-emerald-400 whitespace-nowrap">
                          {new Date(reg.checked_in_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
